import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Upload, LineChart, LogOut, Flame, Sparkles, ShieldCheck, MessageCircle, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import UpgradeModal from "@/components/UpgradeModal";
import FeedbackModal from "@/components/FeedbackModal";

const baseNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/upload", label: "New Exam", icon: Upload, testid: "nav-upload" },
  { to: "/history", label: "History", icon: LineChart, testid: "nav-history" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [quota, setQuota] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api.get("/user/quota").then((r) => setQuota(r.data)).catch(() => {});
    api.get("/admin/me").then((r) => setIsAdmin(!!r.data.is_admin)).catch(() => setIsAdmin(false));
  }, [loc.pathname]);

  const nav = baseNav;

  return (
    <div className="min-h-screen bg-stone-50">
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col border-r border-stone-200 bg-white lg:flex">
        <div className="flex items-center gap-2 border-b border-stone-200 px-6 py-5">
          <div className="flex h-8 w-8 items-center justify-center rounded bg-orange-600 text-white">
            <Flame className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading text-lg font-bold tracking-tight">ExamBro</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">v0.1 · mvp</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4">
          {nav.map((n) => {
            const active = loc.pathname === n.to || (n.to === "/dashboard" && loc.pathname === "/");
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`mb-1 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  active ? "bg-stone-100 text-stone-900" : "text-stone-500 hover:bg-stone-50 hover:text-stone-900"
                }`}
              >
                <Icon className="h-4 w-4" />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-stone-200 p-4">
          {quota && !quota.is_premium && (
            <button
              onClick={() => setShowUpgrade(true)}
              data-testid="sidebar-quota"
              className="w-full mb-3 rounded-md border border-stone-200 bg-stone-50 p-3 text-left hover:border-orange-400 transition-colors"
              style={{ transition: "border-color 0.2s ease" }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Free plan</span>
                <Sparkles className="h-3.5 w-3.5 text-orange-600" />
              </div>
              <div className="font-mono text-xs font-semibold tabular-nums mb-2">
                {quota.used} / {quota.limit} exams used
              </div>
              <div className="h-1 rounded-full bg-stone-200 overflow-hidden">
                <div
                  className="h-full bg-orange-600"
                  style={{ width: `${Math.min(100, (quota.used / quota.limit) * 100)}%` }}
                />
              </div>
              <div className="text-[10px] text-orange-600 mt-2 font-medium">Upgrade →</div>
            </button>
          )}
          <button
            onClick={() => setShowFeedback(true)}
            data-testid="feedback-trigger"
            className="w-full mb-2 flex items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-xs font-medium text-stone-600 hover:border-orange-400 hover:text-orange-600"
            style={{ transition: "border-color 0.2s ease, color 0.2s ease" }}
          >
            <MessageCircle className="h-3.5 w-3.5" />
            Send feedback
          </button>
          <Link
            to="/profile"
            className="mb-3 flex items-center gap-3 rounded-md p-2 -m-2 hover:bg-stone-50"
            style={{ transition: "background-color 0.15s ease" }}
            data-testid="profile-link"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-stone-900 text-sm font-semibold text-white overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-9 w-9 object-cover" />
              ) : (
                user?.name?.[0]?.toUpperCase() || "?"
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium flex items-center gap-1">
                {user?.name}
                {isAdmin && <ShieldCheck className="h-3 w-3 text-orange-600 flex-shrink-0" />}
              </div>
              <div className="truncate text-xs text-stone-500">{user?.email}</div>
            </div>
          </Link>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            size="sm"
            className="w-full justify-start text-stone-500 hover:text-stone-900"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 border-b border-stone-200 bg-white/70 backdrop-blur-xl">
          <div className="flex items-center justify-between px-6 py-4 lg:px-10">
            <div className="flex items-center gap-2 lg:hidden">
              <div className="flex h-8 w-8 items-center justify-center rounded bg-orange-600 text-white">
                <Flame className="h-4 w-4" />
              </div>
              <span className="font-heading text-lg font-bold">ExamBro</span>
            </div>
            <div className="hidden lg:block">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-400">The Strava for exams</div>
              <div className="font-heading text-lg font-semibold text-stone-900">
                Welcome back, {user?.name?.split(" ")[0]}
              </div>
            </div>
            <div className="flex gap-2 lg:hidden">
              {nav.map((n) => {
                const Icon = n.icon;
                return (
                  <Link key={n.to} to={n.to} className="rounded-md p-2 text-stone-500 hover:bg-stone-100">
                    <Icon className="h-5 w-5" />
                  </Link>
                );
              })}
            </div>
          </div>
        </header>
        <main className="px-6 py-8 lg:px-10 lg:py-10">{children}</main>
      </div>
      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} quota={quota} />
      <FeedbackModal open={showFeedback} onOpenChange={setShowFeedback} />
    </div>
  );
}
